import React, { FC, useState, useRef, useEffect } from 'react';
import classnames from 'classnames';

import styles from './index.module.css';

export interface RSClampedTextProps {
  text: string;
  maxLine: number;
  isClampIfNeeded: boolean;
  onIsClampedChange: (isClamped: boolean) => void;
}

export const RSClampedText: FC<RSClampedTextProps> = ({
  text,
  maxLine,
  isClampIfNeeded,
  onIsClampedChange,
}) => {
  // レンダリングされるまでisClampedがわからないため、undefinedで初期化
  const [isClamped, setIsClamped] = useState<boolean | undefined>(undefined);

  const rootRef = useRef<HTMLDivElement>(null);

  // onIsClampedChangeの参照先の変更時にonIsChangedが発火しないようにrefで扱う
  const onIsClampedChangeRef = useRef(onIsClampedChange);
  useEffect(() => {
    onIsClampedChangeRef.current = onIsClampedChange;
  }, [onIsClampedChange]);

  useEffect(() => {
    if (typeof isClamped === 'undefined') return;

    onIsClampedChangeRef.current(isClamped);
  }, [isClamped]);

  useEffect(() => {
    const rootElement = rootRef.current;

    if (!rootElement) return;

    const updateIsClampedIfNeeded = () => {
      const element = rootRef.current;
      if (!element) return;

      // scrollHeightは要素本来の高さを示す
      const scrollHeight = element.scrollHeight;
      // clientHeightは実際にレンダリングされた高さを示す
      const contentHeight = element.clientHeight;

      setIsClamped(scrollHeight > contentHeight);
    };

    // 要素のサイズの変化を監視する
    const resizeObserver = new ResizeObserver(() => {
      updateIsClampedIfNeeded();
    });
    resizeObserver.observe(rootElement);

    // textが動的に変わる可能性があるため、要素の内容の変化を監視する
    const mutationObserver = new MutationObserver(() => {
      updateIsClampedIfNeeded();
    });
    mutationObserver.observe(rootElement, {
      subtree: true,
      characterData: true,
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className={classnames(isClampIfNeeded && styles['root'])}
      style={{
        WebkitLineClamp: isClampIfNeeded ? maxLine : undefined,
      }}
    >
      {text}
    </div>
  );
};
